// TFVF Assets Library — download-only registry of the approved
// source documents (manuals, playbooks, retraining deck etc.)
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Download, FileText, FileSpreadsheet } from 'lucide-react';
import { API, authHeaders } from './tfvfShared';
import { useAuth } from '../../../contexts/AuthContext';

const humanSize = (bytes) => {
  if (!bytes) return '';
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

const iconFor = (mime) =>
  mime.includes('spreadsheet') ? FileSpreadsheet : FileText;

const TfvfAssets = () => {
  const { token } = useAuth();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/tfvf/assets`, { headers: authHeaders(token) })
      .then(r => setAssets(r.data?.assets || []))
      .finally(() => setLoading(false));
  }, [token]);

  const download = async (a) => {
    const r = await fetch(`${API}${a.download_path}`, { headers: authHeaders(token) });
    const b = await r.blob();
    const u = URL.createObjectURL(b);
    const link = document.createElement('a');
    link.href = u; link.download = a.filename; link.click();
    URL.revokeObjectURL(u);
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[color:var(--tf-forest)]/50" /></div>;

  return (
    <div className="space-y-4" data-testid="tfvf-assets-page">
      <div className="tf-card rounded-xl p-5">
        <p className="tf-eyebrow">Asset library</p>
        <h2 className="text-xl font-bold text-[color:var(--tf-forest)] mt-1">Approved Total Fusion Source Documents</h2>
        <p className="text-xs text-[color:var(--tf-muted)] mt-1">
          Original files as supplied. The readable in-platform module content is derived from these. Keep the file version aligned with the module version shown.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {assets.map(a => {
          const Icon = iconFor(a.mime);
          return (
            <button
              key={a.id}
              onClick={() => download(a)}
              className="tf-card rounded-lg p-4 flex items-start gap-3 text-left hover:shadow-md transition-shadow"
              data-testid={`tfvf-asset-${a.id}`}
            >
              <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(200,151,74,0.12)' }}>
                <Icon className="w-5 h-5" style={{ color: 'var(--tf-gold-2)' }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[color:var(--tf-forest)] truncate">{a.title}</p>
                <p className="text-xs text-[color:var(--tf-muted)] truncate">{a.filename}</p>
                <p className="text-[10px] text-[color:var(--tf-muted)] mt-1">
                  v{a.version} · {humanSize(a.size_bytes)}
                  {a.module_ids?.length > 0 && <> · used in {a.module_ids.join(', ')}</>}
                </p>
              </div>
              <Download className="w-4 h-4 text-[color:var(--tf-forest)]/40 shrink-0" />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TfvfAssets;
